import React, { useState } from 'react';
import {
  TextField,
  Button,
  Paper,
  Typography,
  CircularProgress,
  Box,
} from '@mui/material';
import { videoService, VideoMetadata } from '../services/api';
import { parseXPostUrls } from '../services/mediaUtils';

interface UrlInputProps {
  onVideoInfo: (info: VideoMetadata) => void;
  setError: (error: string | null) => void;
  /** Called when the input contains multiple post URLs. */
  onBatchUrls?: (urls: string[]) => void;
}

const looksLikeBatch = (raw: string): boolean => {
  if (raw.includes('\n')) return true;
  return parseXPostUrls(raw).length > 1;
};

const UrlInput: React.FC<UrlInputProps> = ({
  onVideoInfo,
  setError,
  onBatchUrls,
}) => {
  const [url, setUrl] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const batchMode = looksLikeBatch(url);

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUrl(e.target.value);
  };

  const handleSearch = async () => {
    const raw = url.trim();
    if (!raw) {
      setError('Please enter an X post URL');
      return;
    }

    const parsed = parseXPostUrls(raw);

    if (parsed.length === 0) {
      const urlPattern = /^(https?:\/\/)?(www\.)?(twitter\.com|x\.com)\/.+/;
      if (!urlPattern.test(raw.split(/\s+/)[0] || raw)) {
        setError('Please enter a valid X post URL');
        return;
      }
      setError('Could not find a status URL. Use a link like x.com/user/status/…');
      return;
    }

    const tokenCount = (raw.match(/status\/\d+/gi) || []).length;
    const softWarning =
      tokenCount > parsed.length
        ? `Queued ${parsed.length} valid post${parsed.length === 1 ? '' : 's'}; skipped ${tokenCount - parsed.length} duplicate or invalid link${tokenCount - parsed.length === 1 ? '' : 's'}.`
        : null;

    // Multiple status URLs → batch mode (when callback provided)
    if (parsed.length > 1 && onBatchUrls) {
      setError(softWarning);
      onBatchUrls(parsed);
      setUrl('');
      return;
    }

    // Single URL — existing fetch flow
    setLoading(true);
    setError(null);

    try {
      const videoInfo = await videoService.getVideoInfo(parsed[0]);
      onVideoInfo(videoInfo);
      setUrl('');
    } catch (error) {
      setError(error instanceof Error ? error.message : 'An unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Enter alone submits; Shift+Enter inserts a newline
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSearch();
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 0,
        backgroundColor: '#16181C',
        border: '1px solid #2F3336',
        borderRadius: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 2 }}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, letterSpacing: '-0.01em' }}
        >
          Paste one or more X post URLs
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          alignItems: { sm: 'flex-start' },
        }}
      >
        <TextField
          fullWidth
          multiline
          minRows={1}
          maxRows={6}
          variant="outlined"
          placeholder="Paste one or many X post URLs (one per line)…"
          value={url}
          onChange={handleUrlChange}
          onKeyDown={handleKeyDown}
          disabled={loading}
          aria-label="X post URL input"
          helperText="Enter submits · Shift+Enter for a new line · multiple URLs queue a batch"
          FormHelperTextProps={{
            sx: { color: '#71767B', mx: 0, mt: 1 },
          }}
          sx={{
            '& .MuiInputBase-root': {
              py: 1,
            },
            '& .MuiInputBase-input': {
              lineHeight: 1.5,
            },
          }}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handleSearch}
          disabled={loading || !url.trim()}
          aria-label={batchMode ? 'Queue posts' : 'Get media'}
          sx={{
            minWidth: { sm: 140 },
            height: 40,
            px: 3,
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: '9999px',
            flexShrink: 0,
            alignSelf: { sm: 'flex-start' },
            mt: { sm: '4px' },
            transition: 'transform 160ms ease, box-shadow 160ms ease',
            '&:hover:not(:disabled)': {
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 16px rgba(29, 155, 240, 0.35)',
            },
          }}
        >
          {loading ? (
            <CircularProgress size={22} color="inherit" />
          ) : batchMode ? (
            'Queue Posts'
          ) : (
            'Get Media'
          )}
        </Button>
      </Box>
    </Paper>
  );
};

export default UrlInput;
